/**
 * Unified Upload Zone
 *
 * Single dropzone for the tariff classifier upload flow. Accepts a mix of:
 *  - Tabular files (.csv, .xlsx, .xls) — routed through the bulk-upload path
 *    which does column mapping when auto-detection fails.
 *  - Unstructured files (.pdf, .jpg, .png) — routed through the document
 *    classification path, with an optional per-file description.
 *
 * Replaces the previous File Upload, Image Classification, and Bulk Upload tabs.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Description as CsvIcon,
  TableChart as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  DragIndicator as DragHandleIcon,
} from '@mui/icons-material';
import { brand } from '../../../theme';
import { stepperIcons } from '../../../assets/icons/nav';
import NavIcon from '../../../shared/components/NavIcon';
import { useDropzone } from 'react-dropzone';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export type QueuedKind = 'tabular' | 'unstructured';

export interface QueuedFile {
  id: string;
  file: File;
  kind: QueuedKind;
  description: string;
  // Blob URL for image preview. Only set for .jpg/.jpeg/.png; revoked on removal.
  thumbnailUrl?: string;
}

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

const isImageFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

interface UnifiedUploadZoneProps {
  onProcess: (queue: QueuedFile[]) => Promise<void>;
  processing?: boolean;
  maxFiles?: number;
  maxTotalSizeMB?: number;
}

const TABULAR_EXTENSIONS = ['.csv', '.xls', '.xlsx'];
const UNSTRUCTURED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png'];

const getKind = (file: File): QueuedKind | null => {
  const name = file.name.toLowerCase();
  if (TABULAR_EXTENSIONS.some((ext) => name.endsWith(ext))) return 'tabular';
  if (UNSTRUCTURED_EXTENSIONS.some((ext) => name.endsWith(ext))) return 'unstructured';
  return null;
};

const getIcon = (file: File) => {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv')) return <CsvIcon color="primary" />;
  if (name.endsWith('.xls') || name.endsWith('.xlsx')) return <ExcelIcon color="success" />;
  if (name.endsWith('.pdf')) return <PdfIcon color="error" />;
  if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png'))
    return <ImageIcon color="info" />;
  return <FileIcon />;
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

interface SortableRowProps {
  q: QueuedFile;
  processing: boolean;
  onRemove: (id: string) => void;
  onDescriptionChange: (id: string, description: string) => void;
}

/**
 * Single queue row, wired into @dnd-kit/sortable. The drag listeners live on
 * the grip icon only (handle-based drag) so the description TextField and
 * delete button stay clickable. The PointerSensor's 6px activation distance
 * still keeps brief clicks from misfiring as drags.
 */
const SortableRow: React.FC<SortableRowProps> = ({
  q,
  processing,
  onRemove,
  onDescriptionChange,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: q.id, disabled: processing });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Lift the dragged card so it floats above the rest of the list
    zIndex: isDragging ? 10 : 'auto',
    position: 'relative',
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 1.5,
          pl: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: isDragging ? brand.orange : brand.borderSubtle,
          bgcolor: 'background.paper',
          boxShadow: isDragging
            ? '0 8px 24px rgba(0,0,0,0.12)'
            : '0 1px 2px rgba(0,0,0,0.04)',
          transition: 'box-shadow 0.18s, border-color 0.18s',
          opacity: isDragging ? 0.95 : 1,
          '&:hover': {
            borderColor: isDragging ? brand.orange : brand.borderSubtle,
            boxShadow: isDragging
              ? '0 8px 24px rgba(0,0,0,0.12)'
              : '0 2px 8px rgba(0,0,0,0.06)',
            '& .row-drag-handle': { color: 'text.secondary' },
            '& .row-delete': { opacity: 1 },
          },
        }}
      >
        {/* Drag handle — both listeners AND attributes live here so keyboard
            focus + screen-reader announcements target the actual grip, not the
            full row (per @dnd-kit handle-based pattern). */}
        <Box
          className="row-drag-handle"
          {...listeners}
          {...attributes}
          sx={{
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            color: 'text.disabled',
            cursor: processing ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            transition: 'color 0.15s',
            px: 0.25,
            outline: 'none',
            '&:focus-visible': {
              color: brand.orange,
              outline: '2px solid',
              outlineColor: brand.orange,
              outlineOffset: 2,
              borderRadius: 1,
            },
            '&:hover': {
              color: processing ? 'text.disabled' : 'text.primary',
            },
          }}
          aria-label={`Drag to reorder ${q.file.name}`}
        >
          <DragHandleIcon fontSize="small" />
        </Box>

        {/* Thumbnail / file-type icon */}
        <Box
          sx={{
            width: 56,
            height: 56,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1.5,
            bgcolor: q.thumbnailUrl ? 'transparent' : brand.dropZoneBg,
            border: q.thumbnailUrl ? '1px solid' : 'none',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          {q.thumbnailUrl ? (
            <Box
              component="img"
              src={q.thumbnailUrl}
              alt={q.file.name}
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Box sx={{ fontSize: 28, display: 'flex' }}>{getIcon(q.file)}</Box>
          )}
        </Box>

        {/* Title, metadata, optional description */}
        <Box sx={{ flexGrow: 1, minWidth: 0, pt: 0.25 }}>
          <Typography
            variant="body1"
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
            noWrap
            title={q.file.name}
          >
            {q.file.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25 }}
          >
            {formatSize(q.file.size)} ·{' '}
            {q.kind === 'tabular' ? 'spreadsheet' : 'document / image'}
          </Typography>

          {q.kind === 'unstructured' && (
            <TextField
              placeholder="Optional context — brand, material, intended use…"
              size="small"
              fullWidth
              multiline
              maxRows={3}
              value={q.description}
              onChange={(e) => onDescriptionChange(q.id, e.target.value)}
              disabled={processing}
              inputProps={{ maxLength: 5000 }}
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        {/* Delete (top-right, fades in on hover for less visual noise) */}
        <IconButton
          className="row-delete"
          onClick={() => onRemove(q.id)}
          size="small"
          disabled={processing}
          aria-label={`Remove ${q.file.name}`}
          sx={{
            opacity: 0.5,
            transition: 'opacity 0.15s',
            color: 'text.secondary',
            '&:hover': { color: 'error.main', opacity: 1 },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

const UnifiedUploadZone: React.FC<UnifiedUploadZoneProps> = ({
  onProcess,
  processing = false,
  maxFiles = 10,
  maxTotalSizeMB = 50,
}) => {
  const [queue, setQueue] = useState<QueuedFile[]>([]);
  const [error, setError] = useState<string | null>(null);

  // dnd-kit sensors: pointer for mouse/touch, keyboard for accessibility.
  // PointerSensor's activation constraint requires 6px of movement before drag
  // starts, so a normal click doesn't accidentally initiate a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setQueue((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  }, []);

  const totalSize = queue.reduce((sum, q) => sum + q.file.size, 0);

  // Track every blob URL we mint so we can revoke them on unmount or queue change.
  const objectUrlsRef = useRef<Set<string>>(new Set());

  // Revocation runs AFTER each render that changes the queue, so the <img>
  // elements pointing at to-be-freed URLs are already unmounted by the time we
  // revoke. Avoids the broken-image flash that synchronous revocation caused.
  useEffect(() => {
    const activeUrls = new Set<string>();
    for (const q of queue) {
      if (q.thumbnailUrl) activeUrls.add(q.thumbnailUrl);
    }
    for (const url of Array.from(objectUrlsRef.current)) {
      if (!activeUrls.has(url)) {
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(url);
      }
    }
  }, [queue]);

  // Final cleanup if the component unmounts with files still queued.
  useEffect(
    () => () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    },
    []
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError(null);

      const remainingSlots = maxFiles - queue.length;
      let toAdd = acceptedFiles;
      if (acceptedFiles.length > remainingSlots) {
        setError(`Only ${remainingSlots} more file(s) can be added (max ${maxFiles}).`);
        toAdd = acceptedFiles.slice(0, remainingSlots);
      }

      const newSize = toAdd.reduce((sum, f) => sum + f.size, 0);
      if (totalSize + newSize > maxTotalSizeMB * 1024 * 1024) {
        setError(`Total upload size would exceed ${maxTotalSizeMB} MB.`);
        return;
      }

      const additions: QueuedFile[] = [];
      for (const file of toAdd) {
        const kind = getKind(file);
        if (!kind) continue;
        let thumbnailUrl: string | undefined;
        if (isImageFile(file)) {
          thumbnailUrl = URL.createObjectURL(file);
          objectUrlsRef.current.add(thumbnailUrl);
        }
        additions.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          file,
          kind,
          description: '',
          thumbnailUrl,
        });
      }
      setQueue((prev) => [...prev, ...additions]);
    },
    [queue.length, totalSize, maxFiles, maxTotalSizeMB]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: true,
    disabled: processing,
  });

  const removeFile = (id: string) => {
    // The diff useEffect above will revoke this file's URL after re-render.
    setQueue((prev) => prev.filter((q) => q.id !== id));
  };

  const updateDescription = (id: string, description: string) => {
    setQueue((prev) => prev.map((q) => (q.id === id ? { ...q, description } : q)));
  };

  const clearAll = () => {
    setQueue([]);
    setError(null);
  };

  const handleProcessClick = async () => {
    if (queue.length === 0) return;
    setError(null);
    try {
      await onProcess(queue);
      setQueue([]);
    } catch (err) {
      // Parent surfaces toast errors; queue is preserved so the user can retry.
    }
  };

  const tabularCount = queue.filter((q) => q.kind === 'tabular').length;
  const unstructuredCount = queue.filter((q) => q.kind === 'unstructured').length;

  return (
    <Box>
      <Paper
        elevation={0}
        {...getRootProps()}
        sx={{
          p: 4,
          border: '2px dashed',
          borderColor: isDragActive ? brand.orange : brand.borderSubtle,
          bgcolor: brand.dropZoneBg,
          borderRadius: 1,
          cursor: processing ? 'not-allowed' : 'pointer',
          transition: 'all 0.3s ease',
          textAlign: 'center',
          '&:hover': {
            borderColor: processing ? brand.borderSubtle : brand.orange,
          },
        }}
      >
        <input {...getInputProps()} />
        <NavIcon src={stepperIcons.dragDrop} size={48} sx={{ mx: 'auto', mb: 1 }} />
        <Typography variant="body1" gutterBottom sx={{ color: brand.textPrimary, fontWeight: 500 }}>
          {isDragActive ? 'Drop files to add to the queue' : 'Drag & drop files here'}
        </Typography>
        <Typography variant="caption" sx={{ color: brand.textSecondary, display: 'block', mb: 2 }}>
          or click to browse
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip label="CSV" size="small" variant="outlined" />
          <Chip label="Excel" size="small" variant="outlined" />
          <Chip label="PDF" size="small" variant="outlined" />
          <Chip label="JPG / PNG" size="small" variant="outlined" />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Up to {maxFiles} files, {maxTotalSizeMB} MB total (10 MB per file).
        </Typography>
      </Paper>

      {fileRejections.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {fileRejections.map(({ file, errors }) => (
            <Typography variant="caption" component="div" key={file.name}>
              <strong>{file.name}</strong>:{' '}
              {errors
                .map((e) =>
                  e.code === 'file-invalid-type'
                    ? 'Unsupported format. Use CSV, Excel, PDF, JPG, or PNG.'
                    : e.message
                )
                .join(', ')}
            </Typography>
          ))}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {queue.length > 0 && (
        <Paper sx={{ mt: 2, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              {queue.length} of {maxFiles} files queued
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatSize(totalSize)} / {maxTotalSizeMB} MB
              {tabularCount > 0 && unstructuredCount > 0
                ? ` • ${tabularCount} tabular, ${unstructuredCount} document/image`
                : ''}
            </Typography>
          </Box>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={queue.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              {queue.map((q) => (
                <SortableRow
                  key={q.id}
                  q={q}
                  processing={processing}
                  onRemove={removeFile}
                  onDescriptionChange={updateDescription}
                />
              ))}
            </SortableContext>
          </DndContext>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleProcessClick}
              disabled={processing || queue.length === 0}
              startIcon={
                processing ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />
              }
              sx={{
                backgroundColor: brand.black,
                color: '#fff',
                py: 1.2,
                px: 3,
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 1,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#000' },
                '&.Mui-disabled': { backgroundColor: '#3A3A3A', color: '#888' },
              }}
            >
              {processing ? 'Processing…' : `Process all (${queue.length})`}
            </Button>
            <Button
              variant="outlined"
              onClick={clearAll}
              disabled={processing}
              sx={{
                borderColor: brand.borderSubtle,
                color: brand.textPrimary,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 1,
                '&:hover': { borderColor: brand.orange, backgroundColor: 'transparent' },
              }}
            >
              Clear
            </Button>
          </Stack>
        </Paper>
      )}

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="caption" component="div">
          • <strong>CSV / Excel</strong>: rows are classified individually. If headers aren't
          standard, you'll be asked to pick the description column.
        </Typography>
        <Typography variant="caption" component="div">
          • <strong>PDF / Image</strong>: classified as a single product. Add optional context
          (brand, material, etc.) to improve accuracy.
        </Typography>
      </Alert>
    </Box>
  );
};

export default UnifiedUploadZone;
